/**
 * Grade Conversion Utilities
 *
 * Bidirectional conversion between V-scale and Font scale grading systems.
 *
 * V-scale (Vermin Scale) is primarily used in the USA.
 * Font scale (Fontainebleau) is primarily used in Europe.
 *
 * Each V-grade maps to a primary Font grade, but also "includes" any
 * in-between Font grades up to the next V-grade:
 *
 * VB = 3
 * V0 = 4, 4-  (4+ is in-between, included in V0)
 * V1 = 5
 * V2 = 5+
 * V3 = 6A    (6A+ is in-between, included in V3)
 * V4 = 6B    (6B+ is in-between, included in V4)
 * V5 = 6C    (6C+ is in-between, included in V5)
 * V6 = 7A
 * V7 = 7A+
 * V8 = 7B    (7B+ is in-between, included in V8)
 * V9 = 7C
 * V10 = 7C+
 * V11 = 8A
 * V12 = 8A+
 * V13 = 8B
 * V14 = 8B+
 * V15 = 8C
 * V16 = 8C+
 * V17 = 9A
 */

import type { VGrade, GradeModifier } from '@flash/shared';

/**
 * Primary Font grades that map 1:1 with V_GRADES.
 * Used for display when converting V-grade to Font.
 */
export const FONT_GRADES = [
  '3', // VB
  '4', // V0
  '5', // V1
  '5+', // V2
  '6A', // V3
  '6B', // V4
  '6C', // V5
  '7A', // V6
  '7A+', // V7
  '7B', // V8
  '7C', // V9
  '7C+', // V10
  '8A', // V11
  '8A+', // V12
  '8B', // V13
  '8B+', // V14
  '8C', // V15
  '8C+', // V16
  '9A', // V17
] as const;

/**
 * All Font grades including in-between grades, in ascending order.
 * In-between grades (like 6A+) are associated with the lower V-grade.
 */
export const ALL_FONT_GRADES = [
  '3', // VB
  '4-', // V0
  '4', // V0
  '4+', // V0
  '5', // V1
  '5+', // V2
  '6A', // V3
  '6A+', // V3
  '6B', // V4
  '6B+', // V4
  '6C', // V5
  '6C+', // V5
  '7A', // V6
  '7A+', // V7
  '7B', // V8
  '7B+', // V8
  '7C', // V9
  '7C+', // V10
  '8A', // V11
  '8A+', // V12
  '8B', // V13
  '8B+', // V14
  '8C', // V15
  '8C+', // V16
  '9A', // V17
] as const;

/**
 * Maps every Font grade (including in-betweens) to its associated V-grade.
 * In-between grades map to the lower V-grade.
 */
export const FONT_TO_V_MAP: Record<string, VGrade> = {
  '3': 'VB',
  '4-': 'V0',
  '4': 'V0',
  '4+': 'V0',
  '5': 'V1',
  '5+': 'V2',
  '6A': 'V3',
  '6A+': 'V3',
  '6B': 'V4',
  '6B+': 'V4',
  '6C': 'V5',
  '6C+': 'V5',
  '7A': 'V6',
  '7A+': 'V7',
  '7B': 'V8',
  '7B+': 'V8',
  '7C': 'V9',
  '7C+': 'V10',
  '8A': 'V11',
  '8A+': 'V12',
  '8B': 'V13',
  '8B+': 'V14',
  '8C': 'V15',
  '8C+': 'V16',
  '9A': 'V17',
};

/**
 * Font grade type representing valid Fontainebleau grades (primary only).
 */
export type FontGrade = (typeof FONT_GRADES)[number];

/**
 * All Font grade type including in-between grades.
 */
export type AllFontGrade = (typeof ALL_FONT_GRADES)[number];

/**
 * V-grades array matching the FONT_GRADES index positions.
 */
const V_GRADES: VGrade[] = [
  'VB',
  'V0',
  'V1',
  'V2',
  'V3',
  'V4',
  'V5',
  'V6',
  'V7',
  'V8',
  'V9',
  'V10',
  'V11',
  'V12',
  'V13',
  'V14',
  'V15',
  'V16',
  'V17',
];

/**
 * Type guard to check if a value is a valid primary FontGrade.
 */
export const isFontGrade = (value: unknown): value is FontGrade => {
  return typeof value === 'string' && FONT_GRADES.includes(value as FontGrade);
};

/**
 * Type guard to check if a value is any valid Font grade (including in-betweens).
 */
export const isAnyFontGrade = (value: unknown): value is AllFontGrade => {
  return typeof value === 'string' && value in FONT_TO_V_MAP;
};

/**
 * Convert a V-grade to its Font equivalent.
 *
 * @param vGrade - The V-grade to convert (V0-V17)
 * @returns The corresponding Font grade
 *
 * @example
 * ```ts
 * vToFont('V4') // Returns '6b'
 * vToFont('V10') // Returns '7c+'
 * ```
 */
export const vToFont = (vGrade: VGrade): FontGrade => {
  const index = V_GRADES.indexOf(vGrade);
  // Index is guaranteed valid since VGrade is constrained to valid values
  return FONT_GRADES[index] as FontGrade;
};

/**
 * Convert a Font grade to its V-scale equivalent.
 * Handles both primary Font grades and in-between grades.
 * In-between grades (like 6A+) map to the lower V-grade (V3).
 *
 * @param fontGrade - The Font grade to convert (any valid Font grade)
 * @returns The corresponding V-grade
 *
 * @example
 * ```ts
 * fontToV('6B') // Returns 'V4'
 * fontToV('6B+') // Returns 'V4' (in-between, maps to lower)
 * fontToV('7C+') // Returns 'V10'
 * ```
 */
export const fontToV = (fontGrade: string): VGrade => {
  // Handle case-insensitive lookup
  const normalizedGrade = fontGrade.toUpperCase();
  const vGrade = FONT_TO_V_MAP[normalizedGrade];
  if (vGrade) {
    return vGrade;
  }
  // Fallback for primary grades using index lookup
  const index = FONT_GRADES.findIndex((g) => g.toUpperCase() === normalizedGrade);
  if (index !== -1) {
    return V_GRADES[index] as VGrade;
  }
  // Default to V0 if grade not found
  console.warn(`Unknown Font grade: ${fontGrade}, defaulting to V0`);
  return 'V0';
};

/**
 * Format a V-grade with an optional modifier.
 *
 * @param grade - The base V-grade (V0-V17)
 * @param modifier - Optional modifier ('+', '-', or null)
 * @returns Formatted grade string (e.g., "V4+", "V5-", "V3")
 *
 * @example
 * ```ts
 * formatGradeWithModifier('V4', '+') // Returns 'V4+'
 * formatGradeWithModifier('V5', '-') // Returns 'V5-'
 * formatGradeWithModifier('V3', null) // Returns 'V3'
 * ```
 */
export const formatGradeWithModifier = (grade: VGrade, modifier: GradeModifier | null): string => {
  if (!modifier) {
    return grade;
  }
  return `${grade}${modifier}`;
};

/**
 * Parse a grade string that may contain a modifier.
 *
 * @param gradeString - Grade string (e.g., "V4+", "V5-", "V3")
 * @returns Object with parsed grade and modifier, or null if invalid
 *
 * @example
 * ```ts
 * parseGradeWithModifier('V4+') // Returns { grade: 'V4', modifier: '+' }
 * parseGradeWithModifier('V5-') // Returns { grade: 'V5', modifier: '-' }
 * parseGradeWithModifier('V3')  // Returns { grade: 'V3', modifier: null }
 * parseGradeWithModifier('invalid') // Returns null
 * ```
 */
export const parseGradeWithModifier = (
  gradeString: string
): { grade: VGrade; modifier: GradeModifier } | null => {
  // Match V-scale pattern: Vb or V followed by digits, optionally followed by + or -
  const match = gradeString.match(/^(VB|V\d+)([+-])?$/);
  if (!match) {
    return null;
  }

  const [, gradeStr, modifierStr] = match;

  // Validate the grade is a valid VGrade
  if (!V_GRADES.includes(gradeStr as VGrade)) {
    return null;
  }

  const modifier: GradeModifier = modifierStr === '+' ? '+' : modifierStr === '-' ? '-' : null;

  return {
    grade: gradeStr as VGrade,
    modifier,
  };
};

/**
 * Convert a V-grade with modifier to the specified display format.
 *
 * For V-scale output, preserves the modifier (e.g., "V4+").
 * For Font scale output, adds the modifier to the converted grade (e.g., "6b+").
 *
 * @param grade - The base V-grade
 * @param modifier - Optional modifier
 * @param targetSystem - Target grading system ('v-scale' or 'font')
 * @returns Formatted grade string in the target system
 *
 * @example
 * ```ts
 * convertWithModifier('V4', '+', 'v-scale') // Returns 'V4+'
 * convertWithModifier('V4', '+', 'font')    // Returns '6b+'
 * convertWithModifier('V3', null, 'font')   // Returns '6a'
 * ```
 */
export const convertWithModifier = (
  grade: VGrade,
  modifier: GradeModifier | null,
  targetSystem: 'v-scale' | 'font'
): string => {
  if (targetSystem === 'v-scale') {
    return formatGradeWithModifier(grade, modifier);
  }

  // Convert to Font scale
  const fontGrade = vToFont(grade);
  if (!modifier) {
    return fontGrade;
  }
  return `${fontGrade}${modifier}`;
};
